import { useEffect, useState, type RefObject } from "react";

/**
 * Returns a sticky `top` value (px) that pins a section so its BOTTOM rests at
 * the viewport bottom once it has fully scrolled into view — i.e. the section
 * scrolls through normally (all content visible), then sticks for the next
 * section to slide up over it.
 *
 * top = min(0, viewportHeight - sectionHeight):
 *  - section taller than the viewport → negative top → pins the bottom edge
 *  - section == viewport height → 0 → pins the top edge (same thing)
 *
 * Desktop only (md and up). Below md it returns undefined so the section stays
 * in normal flow (the `top` must not offset a `position: relative` element on
 * mobile).
 */
export function useCoverPin(ref: RefObject<HTMLElement>): number | undefined {
  const [top, setTop] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      if (!isDesktop) {
        setTop(undefined);
        return;
      }
      setTop(Math.min(0, window.innerHeight - el.offsetHeight));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [ref]);

  return top;
}
